(() => {
  const settings = window.themeVariables?.settings || {};
  const giftWrapVariantId = Number(settings.friendsGiftWrapVariantId || 0);

  if (!giftWrapVariantId) {
    return;
  }

  let isSynchronizing = false;

  const getCart = async () => {
    const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`, {
      credentials: "same-origin"
    });

    if (!response.ok) {
      throw new Error("Unable to load the cart.");
    }

    return response.json();
  };

  const changeLine = async (payload) => {
    const response = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || "Unable to update gift wrap.");
    }

    return response.json();
  };

  const addGiftWrap = async (quantity) => {
    const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: giftWrapVariantId, quantity }] })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || "Gift wrap is currently unavailable.");
    }
  };

  const expectedGiftWrapQuantity = (cart) => cart.items.reduce((quantity, item) => {
    if (item.id === giftWrapVariantId) {
      return quantity;
    }

    return item.properties?.giftwrap === "yes" ? quantity + item.quantity : quantity;
  }, 0);

  const displayedItemCount = (cart) => cart.items.reduce((quantity, item) => {
    return item.id === giftWrapVariantId ? quantity : quantity + item.quantity;
  }, 0);

  const updateDisplayedItemCounts = (cart) => {
    const count = displayedItemCount(cart);
    document.querySelectorAll("cart-count").forEach((element) => {
      element.textContent = count;
    });
  };

  const announce = (toggle, message) => {
    const status = toggle.closest("[data-friends-gift-wrap]")?.querySelector("[role='status']");

    if (status) {
      status.textContent = message;
    }
  };

  const dispatchCartUpdate = (cart, replacementDelay = 0) => {
    document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
      bubbles: true,
      detail: { cart }
    }));

    document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
      bubbles: true,
      detail: { cart, replacementDelay }
    }));
  };

  const synchronizeGiftWrap = async (cart) => {
    const expectedQuantity = expectedGiftWrapQuantity(cart);
    const giftWrapLine = cart.items.find((item) => item.id === giftWrapVariantId);
    const actualQuantity = giftWrapLine?.quantity || 0;

    if (expectedQuantity === actualQuantity) {
      return cart;
    }

    if (giftWrapLine) {
      await changeLine({ id: giftWrapLine.key, quantity: expectedQuantity });
    } else if (expectedQuantity > 0) {
      await addGiftWrap(expectedQuantity);
    }

    return getCart();
  };

  const handleToggle = async (toggle) => {
    const previousChecked = !toggle.checked;
    document.querySelectorAll("[data-friends-gift-wrap-toggle]").forEach((checkbox) => {
      checkbox.disabled = true;
    });
    toggle.closest("[data-friends-gift-wrap]")?.setAttribute("aria-busy", "true");

    try {
      const cart = await getCart();
      const lineItem = cart.items.find((item) => item.key === toggle.dataset.lineKey);

      if (!lineItem || lineItem.id === giftWrapVariantId) {
        throw new Error("This cart item has changed. Please try again.");
      }

      const properties = { ...(lineItem.properties || {}), giftwrap: toggle.checked ? "yes" : "no" };
      let updatedCart = await changeLine({
        id: lineItem.key,
        quantity: lineItem.quantity,
        properties
      });

      updatedCart = await synchronizeGiftWrap(updatedCart);
      updateDisplayedItemCounts(updatedCart);
      announce(toggle, toggle.checked ? "Gift wrap added." : "Gift wrap removed.");

      if (window.themeVariables.settings.pageType === "cart") {
        window.location.reload();
        return;
      }

      dispatchCartUpdate(updatedCart, 250);
    } catch (error) {
      toggle.checked = previousChecked;
      announce(toggle, error.message);
      document.documentElement.dispatchEvent(new CustomEvent("cart-notification:show", {
        bubbles: true,
        cancelable: true,
        detail: { status: "error", error: error.message }
      }));
    } finally {
      document.querySelectorAll("[data-friends-gift-wrap-toggle]").forEach((checkbox) => {
        checkbox.disabled = false;
      });
      toggle.closest("[data-friends-gift-wrap]")?.removeAttribute("aria-busy");
    }
  };

  document.addEventListener("change", (event) => {
    const toggle = event.target.closest?.("[data-friends-gift-wrap-toggle]");

    if (toggle) {
      handleToggle(toggle);
    }
  });

  document.addEventListener("cart:updated", async (event) => {
    const cart = event.detail?.cart;

    if (!cart) {
      return;
    }

    updateDisplayedItemCounts(cart);

    if (isSynchronizing) {
      return;
    }

    try {
      isSynchronizing = true;
      const synchronizedCart = await synchronizeGiftWrap(cart);

      if (synchronizedCart !== cart) {
        updateDisplayedItemCounts(synchronizedCart);

        if (window.themeVariables.settings.pageType === "cart") {
          window.location.reload();
        } else {
          dispatchCartUpdate(synchronizedCart, 250);
        }
      }
    } catch (error) {
      console.error("Friends gift-wrap synchronization failed:", error);
    } finally {
      isSynchronizing = false;
    }
  });

  document.addEventListener("cart:refresh", async (event) => {
    if (event.detail?.cart) {
      updateDisplayedItemCounts(event.detail.cart);
      return;
    }

    try {
      updateDisplayedItemCounts(await getCart());
    } catch (error) {
      console.error("Friends cart-count refresh failed:", error);
    }
  });

  document.addEventListener("DOMContentLoaded", async () => {
    try {
      const cart = await getCart();
      updateDisplayedItemCounts(cart);
      const synchronizedCart = await synchronizeGiftWrap(cart);

      if (synchronizedCart !== cart) {
        updateDisplayedItemCounts(synchronizedCart);

        if (window.themeVariables.settings.pageType === "cart") {
          window.location.reload();
        } else {
          dispatchCartUpdate(synchronizedCart);
        }
      }
    } catch (error) {
      console.error("Friends gift-wrap initialization failed:", error);
    }
  });
})();
